/* ===== Ascendia: video diet (7 Sep 2026) ====================================
 * The product page used to reference every gallery video twice (gallery copy +
 * zoom-modal copy), always as the HD-1080p file, with autoplay. On the Yoga Flex
 * set that was 167.6 MB of video the phone could start fetching on load.
 *
 * Now the <video> tags carry NO URL (see snippets/product-thumbnail.liquid and
 * snippets/product-media.liquid). This script:
 *   1. reads the product JSON printed by sections/main-product.liquid
 *      (<script data-asc-video-json>), which lists every rendition per video;
 *      if that JSON has no 720p entries it fetches /products/<handle>.js instead;
 *   2. gives a GALLERY video its HD-720p src only when its slide is at least half
 *      on screen AND not hidden by the colour picker / desktop one-slide layout,
 *      then plays it muted; scrolling away pauses it. Slides never shown never load;
 *   3. gives a MODAL video its src the moment global.js clones it out of its
 *      <template> (720p on phones, 1080p on wide screens) and starts it muted.
 *
 * Fails safe: no JSON + no fetch = the video keeps its poster and controls, and
 * nothing else on the page is touched. Respects prefers-reduced-motion and the
 * browser's Data Saver setting by not auto-playing (the play button still works).
 * ========================================================================== */
(function () {
  'use strict';

  var SEL = 'video[data-asc-video]';
  var GALLERY = 720;

  var renditions = null; // media id -> { 480: url, 720: url, 1080: url }
  var pending = [];
  var loading = false;

  var reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var saveData = !!(navigator.connection && navigator.connection.saveData);

  function mediaId(v) {
    return v.getAttribute('data-media-id') || v.getAttribute('data-asc-media-id') || '';
  }

  /* ---- rendition index ------------------------------------------------- */

  function index(list) {
    var out = {};
    if (!list || !list.length) return out;
    for (var i = 0; i < list.length; i++) {
      var m = list[i];
      if (!m || m.media_type !== 'video' || !m.sources || !m.sources.length) continue;
      var r = {};
      for (var j = 0; j < m.sources.length; j++) {
        var s = m.sources[j];
        if (!s || s.format !== 'mp4' || !s.url || !s.height) continue;
        r[s.height] = s.url;
      }
      out[String(m.id)] = r;
    }
    return out;
  }

  function has(idx, height) {
    for (var k in idx) if (idx[k] && idx[k][height]) return true;
    return false;
  }

  function fromInline() {
    var el = document.querySelector('script[data-asc-video-json]');
    if (!el) return null;
    try {
      return index(JSON.parse(el.textContent));
    } catch (e) {
      return null;
    }
  }

  function fromFetch(done) {
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m || typeof window.fetch !== 'function') return done(null);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window
      .fetch(root + 'products/' + m[1] + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { done(p && p.media ? index(p.media) : null); })
      .catch(function () { done(null); });
  }

  function settle(idx) {
    renditions = idx || {};
    var cbs = pending;
    pending = [];
    for (var i = 0; i < cbs.length; i++) cbs[i](renditions);
  }

  function ready(cb) {
    if (renditions) return cb(renditions);
    pending.push(cb);
    if (loading) return;
    loading = true;
    var inline = fromInline();
    if (inline && has(inline, GALLERY)) return settle(inline);
    fromFetch(function (fetched) {
      if (fetched && has(fetched, GALLERY)) return settle(fetched);
      settle(fetched || inline || {});
    });
  }

  function urlFor(v, want) {
    var r = renditions && renditions[mediaId(v)];
    if (!r) return null;
    if (r[want]) return r[want];
    if (r[720]) return r[720];
    if (r[1080]) return r[1080];
    var best = null, bh = 0;
    for (var h in r) {
      if (+h > bh) { bh = +h; best = r[h]; }
    }
    return best;
  }

  /* ---- helpers ---------------------------------------------------------- */

  function slideOf(v) {
    return (v.closest && v.closest('.product__media-item')) || v.parentElement;
  }

  // Hidden by the colour picker (display:none on the slide), by the desktop
  // one-slide layout, or simply not laid out yet.
  function rendered(v) {
    var s = slideOf(v);
    if (s && window.getComputedStyle(s).display === 'none') return false;
    return v.getClientRects().length > 0;
  }

  function ensureSrc(v, want) {
    if (v.getAttribute('data-asc-src')) return true;
    var u = urlFor(v, want);
    if (!u) return false;
    v.setAttribute('data-asc-src', String(want));
    v.src = u;
    return true;
  }

  function play(v) {
    if (reducedMotion || saveData) return;
    var p;
    try {
      p = v.play();
    } catch (e) {
      return;
    }
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  function pause(v) {
    if (v.paused) return;
    try {
      v.pause();
    } catch (e) {}
  }

  // "On screen" = at least half the WIDTH is visible (so the next slide peeking
  // in at the edge of the phone slider does not count) AND either half the
  // height is visible or the visible part fills half the screen (so a tall 9:16
  // clip on a short landscape screen still counts).
  function onScreen(box, vis, rootH) {
    if (!box.width || !box.height || vis.width <= 0 || vis.height <= 0) return false;
    if (vis.width / box.width < 0.5) return false;
    return vis.height / box.height >= 0.5 || vis.height >= rootH * 0.5;
  }

  function enoughOnScreen(entry) {
    if (!entry.isIntersecting || !entry.intersectionRect) return false;
    var rootH = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
    return onScreen(entry.boundingClientRect, entry.intersectionRect, rootH);
  }

  function visibleNow(v) {
    if (!rendered(v)) return false;
    var r = v.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var top = Math.max(r.top, 0), bottom = Math.min(r.bottom, vh);
    var left = Math.max(r.left, 0), right = Math.min(r.right, vw);
    return onScreen(r, { width: right - left, height: bottom - top }, vh);
  }

  /* ---- gallery videos: load + play only when on screen ------------------ */

  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          var v = e.target;
          if (enoughOnScreen(e) && rendered(v)) onVisible(v);
          else onHidden(v);
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75] }
    );
  }

  var SETTLE_MS = 300;
  var timers = typeof WeakMap === 'function' ? new WeakMap() : null;
  function getTimer(v) { return timers ? timers.get(v) : v._ascTimer; }
  function setTimer(v, t) { if (timers) timers.set(v, t); else v._ascTimer = t; }
  function cancelTimer(v) {
    var t = getTimer(v);
    if (t) { clearTimeout(t); setTimer(v, null); }
  }

  // Let the layout settle first: switching colour re-flows the slider, and a
  // slide can intersect for a single frame before it scrolls away again. Only a
  // video that is STILL on screen 300 ms later gets its src.
  function onVisible(v) {
    cancelTimer(v);
    setTimer(v, setTimeout(function () {
      setTimer(v, null);
      if (!visibleNow(v)) return;
      ready(function () {
        if (!visibleNow(v)) return; // may have changed while the JSON loaded
        if (ensureSrc(v, GALLERY)) play(v);
      });
    }, SETTLE_MS));
  }

  function onHidden(v) {
    cancelTimer(v);
    pause(v);
  }

  function setupGallery(v) {
    if (io) {
      io.observe(v);
    } else {
      // Very old browser: give it the 720p src so the play button works, no autoplay.
      ready(function () { ensureSrc(v, GALLERY); });
    }
  }

  /* ---- modal videos: appear only when the zoom modal clones the template - */

  function setupModal(v) {
    ready(function () {
      var wide = (window.innerWidth || 0) >= 990;
      if (!ensureSrc(v, wide ? 1080 : GALLERY)) return;
      document.querySelectorAll('video[data-asc-video="gallery"]').forEach(pause);
      play(v);
    });
  }

  // global.js fires modalClosed on <body> when any modal-dialog hides.
  document.body.addEventListener('modalClosed', function () {
    document.querySelectorAll('video[data-asc-video="modal"]').forEach(pause);
    document.querySelectorAll('video[data-asc-video="gallery"]').forEach(function (v) {
      if (v.getAttribute('data-asc-src') && visibleNow(v)) play(v);
    });
  });

  /* ---- wiring ----------------------------------------------------------- */

  function setup(v) {
    if (v.getAttribute('data-asc-setup')) return;
    v.setAttribute('data-asc-setup', '1');
    if (v.getAttribute('data-asc-video') === 'modal') setupModal(v);
    else setupGallery(v);
  }

  function scan(root) {
    var list = (root || document).querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) setup(list[i]);
  }

  // Videos added later: the modal clone, quick-add drawers, section re-renders.
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(SEL)) setup(n);
          else if (n.querySelectorAll) scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }
})();
